import { createSlice } from '@reduxjs/toolkit';
import { PERMISSION_DENIED_MESSAGE } from '../constants/permissions';
import { normalizePermissions } from '../modal-suppression';

const empty = {
  deniedModalOpen: false,
  message: '',
  requiredPermission: '',
  blockedAttemptCount: 0,
  blockedPermissions: [],
  /**
   * Broadcast slot for the most recent permission-denied API settlement.
   * Subscribers (hooks/components) read `lastDenied.id` to detect new denials
   * without coupling to the modal-open state. `null` until the first denial.
   *
   * Shape:
   *   { id, endpoint, requestId, requiredPermission, method, url, ts }
   */
  lastDenied: null,
};

const slice = createSlice({
  name: 'permissionGate',
  initialState: { ...empty },
  reducers: {
    openPermissionDenied: (state, { payload }) => {
      const perm = payload?.requiredPermission || '';
      const msg = payload?.message || PERMISSION_DENIED_MESSAGE;
      const incomingPerms = payload?.blockedPermissions?.length
        ? [...payload.blockedPermissions]
        : perm
          ? [perm]
          : [];

      if (!state.deniedModalOpen) {
        state.deniedModalOpen = true;
        state.message = msg;
        const initialPerms = normalizePermissions(incomingPerms);
        state.requiredPermission = perm || initialPerms[0] || '';
        state.blockedAttemptCount = 1;
        state.blockedPermissions = initialPerms;
        return;
      }
      state.blockedAttemptCount += 1;
      state.blockedPermissions = normalizePermissions([
        ...state.blockedPermissions,
        ...(incomingPerms.length ? incomingPerms : perm ? [perm] : []),
      ]);
    },
    closePermissionDenied: (state) => ({ ...empty, lastDenied: state.lastDenied }),
    /**
     * Broadcasts a single permission-denied API settlement. Independent of the
     * modal (modals may be suppressed/batched). Subscribers should watch
     * `lastDenied.id` (monotonic) to react to new denials.
     */
    permissionRequestDenied: (state, { payload }) => {
      const prevId = state.lastDenied?.id || 0;
      state.lastDenied = {
        id: prevId + 1,
        endpoint: payload?.endpoint ?? null,
        requestId: payload?.requestId ?? null,
        requiredPermission: payload?.requiredPermission ?? null,
        method: payload?.method ?? null,
        url: payload?.url ?? null,
        ts: payload?.ts ?? Date.now(),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase('auth/removeCredentials', () => ({ ...empty }));
  },
});

export const {
  openPermissionDenied,
  closePermissionDenied,
  permissionRequestDenied,
} = slice.actions;
export const selectPermissionDeniedModal = (s) => s.permissionGate;
export const selectLastPermissionDenied = (s) => s.permissionGate.lastDenied;
export default slice.reducer;
